/**
 * resolve — wyliczenie akcji list widocznych w danym widoku (PHASE 25 Faza 1).
 *
 * Czysta logika (bez React) — łatwa do testu. Bierze:
 *  - katalog akcji w kodzie (`@saas/shared`),
 *  - kartotekę (+ opcjonalną klasę dokumentu),
 *  - listę akcji WŁĄCZONYCH w layoucie (`ListConfig.listActions`, brak = zestaw domyślny),
 *  - aktywne moduły tenanta (`useMyModules().subscriptions`),
 *  - uprawnienia usera (`me.permissions`),
 * i zwraca dwie grupy do menu: „na zaznaczonych" i „ogólne".
 *
 * Bezpieczeństwo: to tylko WIDOCZNOŚĆ. Twarde egzekwowanie modułu/uprawnień
 * trzyma backend na endpointach wykonania (bramka istnieje niezależnie).
 */

import {
  defaultListActionIdsFor,
  isActionApplicable,
  LIST_ACTION_CATALOG,
  type ListActionConfig,
  type ListActionDef,
  type ListActionId,
  type ListActionKartotekaKey,
} from '@saas/shared';

export interface ResolveListActionsInput {
  /** Kartoteka listy (klucz osi kategorii lub `'generic'` dla list modułów własnych/kreatorowych). */
  kartotekaKey: ListActionKartotekaKey;
  /** Klasa dokumentu — tylko dla kartoteki `documents` (zawęża KSeF/FK). */
  documentClass?: string;
  /**
   * Akcje włączone w tym layoucie (`ListConfig.listActions`). `undefined` =
   * layout bez konfiguracji → zestaw domyślny. Pusta tablica = świadomie brak akcji.
   */
  enabledActions?: readonly ListActionConfig[];
  /** Kody aktywnych modułów tenanta (`subscriptions[].code`). */
  activeModuleCodes: readonly string[];
  /** Kody uprawnień usera (`me.permissions`). */
  permissions: readonly string[];
  /** Super-user omija bramkę uprawnień (parytet z backendem). */
  isSuperUser?: boolean;
}

export interface ResolvedListActions {
  /** Akcje na zaznaczonych rekordach (sekcja „Na zaznaczonych"). */
  selectionActions: ListActionDef[];
  /** Akcje ogólne, niezależne od zaznaczenia (sekcja „Ogólne"). */
  generalActions: ListActionDef[];
}

/**
 * Czy akcja wymaga zaznaczenia na tej liście. Wartość z katalogu — „Eksport zaznaczonych do CSV"
 * (`export_csv`) wymaga zaznaczenia, „Eksport listy do CSV" (`export_list_csv`) jest ogólny.
 * `kartotekaKey` zostaje w sygnaturze, bo wołają ją miejsca znające kartotekę listy.
 */
export function actionRequiresSelection(
  def: ListActionDef,
  _kartotekaKey: ListActionKartotekaKey,
): boolean {
  return def.requiresSelection;
}

/**
 * Domyślne włączenie: gdy layout nie ma `listActions`, używamy zestawu domyślnego
 * z katalogu — zależnego od kartoteki (faktury dostają domyślnie KSeF/FK, patrz
 * `defaultListActionIdsFor`). I tak przefiltrowane przez moduł+uprawnienie niżej.
 */
function defaultEnabled(
  kartotekaKey: ListActionKartotekaKey,
  documentClass: string | undefined,
): ListActionConfig[] {
  return defaultListActionIdsFor(kartotekaKey, documentClass).map((actionId, order) => ({
    actionId,
    order,
  }));
}

function hasModule(def: ListActionDef, activeModuleCodes: readonly string[]): boolean {
  if (!def.moduleCode) return true;
  return activeModuleCodes.includes(def.moduleCode);
}

function hasPermission(
  def: ListActionDef,
  permissions: readonly string[],
  isSuperUser: boolean,
): boolean {
  if (isSuperUser) return true;
  // `permissionAnyOf` — akcja obejmuje kilka klas o rozłącznych kodach (np. dekretacja faktur
  // zakupu i sprzedaży); wystarczy prawo po jednej ze stron.
  if (def.permissionAnyOf) return def.permissionAnyOf.some((code) => permissions.includes(code));
  if (!def.permission) return true;
  return permissions.includes(def.permission);
}

export function resolveListActions(input: ResolveListActionsInput): ResolvedListActions {
  const {
    kartotekaKey,
    documentClass,
    enabledActions,
    activeModuleCodes,
    permissions,
    isSuperUser = false,
  } = input;

  const enabled = enabledActions ?? defaultEnabled(kartotekaKey, documentClass);

  const selectionActions: ListActionDef[] = [];
  const generalActions: ListActionDef[] = [];
  const seen = new Set<ListActionId>();

  // Zachowaj kolejność z konfiguracji (order), stabilnie.
  const ordered = [...enabled].sort((a, b) => a.order - b.order);

  for (const { actionId } of ordered) {
    if (seen.has(actionId)) continue; // ochrona przed duplikatem w configu
    seen.add(actionId);
    const def = LIST_ACTION_CATALOG[actionId];
    if (!def) continue; // nieznany id (np. usunięty z katalogu) — pomiń
    if (!isActionApplicable(def, kartotekaKey, documentClass)) continue;
    if (!hasModule(def, activeModuleCodes)) continue;
    if (!hasPermission(def, permissions, isSuperUser)) continue;
    (actionRequiresSelection(def, kartotekaKey) ? selectionActions : generalActions).push(def);
  }

  return { selectionActions, generalActions };
}
